import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Clock3, MapPin, Users, Wallet } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { StageReservation } from "@/components/stage-reservation/StageReservation";
import { api, formatCHF } from "@/lib/api";
import { FAQS, IMAGES, OFFERS } from "@/data/site";
import { STAGE_PRICING, STAGE_SUPPLEMENTS } from "@/data/stagePricing";
import { absolute, breadcrumb, useSeo } from "@/lib/seo";

const FALLBACK = { groupe: STAGE_PRICING.groupe, groupe_special: STAGE_PRICING.groupeSpecial, super_lune: STAGE_PRICING.editionSpeciale, individuel: STAGE_PRICING.individuel, privatise_4: STAGE_PRICING.privatise4, exterieur: STAGE_PRICING.exterieur, supplement_domicile: STAGE_PRICING.domicile };
const STAGE_FAQ = FAQS.filter((f) => /stage|peaux|création individuelle|plusieurs fois/i.test(f.q));
const PATH = "/offres/stage-fabrication-tambour";

const TARIFS = [
  ["groupe", "Stage collectif classique", " / pers."],
  ["groupe_special", "Stage collectif — événement spécial (solstice…)", " / pers."],
  ["super_lune", "Édition Super Lune", " / pers."],
  ["individuel", "Individuel ou privatisé à l'atelier (1 à 3 pers.)", " / pers."],
  ["privatise_4", "Privatisé à l'atelier dès 4 personnes", " / pers."],
  ["exterieur", "Individuel sur un lieu énergétique en extérieur", ""],
  ["supplement_domicile", "Individuel ou privatisé à domicile : supplément", " par réservation"],
];

const useStageSeo = (b) => {
  const offer = OFFERS["stage-fabrication-tambour"];
  useSeo({
    title: offer.seoTitle,
    description: `Créez votre tambour traditionnel à l'atelier d'Orbe (VD) : stage collectif dès ${formatCHF(b.groupe)}, individuel ${formatCHF(b.individuel)}, privatisé dès 4 personnes ${formatCHF(b.privatise_4)} / pers. Réservation et paiement en ligne.`,
    image: IMAGES.mains,
    canonicalPath: PATH,
    jsonLd: [
      breadcrumb([["Accueil", "/"], ["Créer son tambour", PATH]]),
      { "@type": "Service", name: offer.titre, description: offer.sousTitre, serviceType: "Stage de création de tambour", provider: { "@id": "https://entrelesmondes.net/#organisation" }, areaServed: ["Suisse romande", "Vaud"], url: absolute(PATH), image: IMAGES.mains,
        offers: TARIFS.filter(([key]) => key !== "supplement_domicile").map(([key, name]) => ({ "@type": "Offer", name, price: b[key], priceCurrency: "CHF", availability: "https://schema.org/InStock", url: absolute(`${PATH}#reserver`) })) },
      { "@type": "FAQPage", mainEntity: STAGE_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.r } })) },
    ],
  });
};

const Infos = ({ b }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-12" data-testid="stage-infos">
    <div className="lg:col-span-7">
      <p className="eyebrow mb-5">Informations pratiques</p>
      <dl className="divide-y divide-sand-dark border-y border-sand-dark text-sm">
        {[[Clock3, "Durée", "1 journée, 10 h – 18 h"], [MapPin, "Lieu", "Atelier Entre Les Mondes, Rue Pierre Viret 8, 1350 Orbe"], [Users, "Groupe", "8 personnes maximum · aucun prérequis"], [Wallet, "Paiement", "TWINT, carte ou facture · acompte de CHF 150.– possible, solde au plus tard le jour du stage"]].map(([Icon, label, value]) => (
          <div key={label} className="py-4 flex gap-4"><Icon className="w-4 h-4 text-terra shrink-0 mt-0.5" /><dt className="font-mono text-xs uppercase tracking-[0.2em] text-fumee w-24 shrink-0">{label}</dt><dd className="text-bark">{value}</dd></div>
        ))}
        <div className="py-4 text-bark leading-relaxed">À apporter : votre pique-nique, un bâton d'environ 50 cm (1 à 2 cm de diamètre) et un t-shirt plein de bons souvenirs — ils deviendront votre mailloche.</div>
      </dl>
    </div>
    <div className="lg:col-span-5" data-testid="stage-tarifs">
      <p className="eyebrow mb-5">Tarifs</p>
      <ul className="divide-y divide-sand-dark border-y border-sand-dark text-sm">
        {TARIFS.map(([key, label, suffix]) => <li key={key} className="py-3 flex justify-between gap-4" data-testid={`stage-tarif-${key}`}><span className="text-bark">{label}</span><strong className="whitespace-nowrap text-bark">{key === "supplement_domicile" ? "+ " : ""}{formatCHF(b[key])}{suffix}</strong></li>)}
      </ul>
      <p className="text-xs text-fumee mt-4 leading-relaxed">Diamètres 40, 45 et 50 cm compris · 35, 55 et 60 cm : + {formatCHF(STAGE_SUPPLEMENTS.diametresHorsStandard)} · autre diamètre : + {formatCHF(STAGE_SUPPLEMENTS.diametreAutre)} (sous réserve de faisabilité). Peaux de cheval, bison ou autruche : + {formatCHF(STAGE_SUPPLEMENTS.peauxPremium)} ; toutes les autres peaux sont comprises.</p>
    </div>
  </section>
);

const MoreInfo = () => (
  <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24" data-testid="stage-more">
    <Accordion type="single" collapsible className="border-y border-sand-dark">
      <AccordionItem value="journee"><AccordionTrigger data-testid="stage-more-journee" className="font-serif text-lg text-bark">Le déroulement de la journée</AccordionTrigger>
        <AccordionContent><ol className="space-y-2 text-sm text-fumee">{OFFERS["stage-fabrication-tambour"].processus.map((p) => <li key={p.titre}><strong className="text-bark">{p.titre}</strong> — {p.texte}</li>)}</ol></AccordionContent></AccordionItem>
      {STAGE_FAQ.map((f, i) => <AccordionItem key={f.q} value={`faq-${i}`}><AccordionTrigger data-testid={`stage-faq-${i}`} className="text-left text-sm text-bark">{f.q}</AccordionTrigger><AccordionContent className="text-sm text-fumee leading-relaxed">{f.r}</AccordionContent></AccordionItem>)}
    </Accordion>
    <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8 text-sm">
      <Link to="/tambours/editions-limitees" data-testid="stage-link-editions" className="inline-flex items-center gap-2 text-terra link-line">Éditions limitées <ArrowRight className="w-4 h-4" /></Link>
      <Link to="/offres/tambours-lieux-anciens" data-testid="stage-link-lieux" className="inline-flex items-center gap-2 text-terra link-line">Tambours des Lieux Anciens <ArrowRight className="w-4 h-4" /></Link>
      <Link to="/faq" data-testid="stage-link-faq" className="inline-flex items-center gap-2 text-terra link-line">Toutes les questions <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </section>
);

const StructuresLink = () => (
  <section className="bg-earth grain text-sand" data-testid="stage-structures-section">
    <div className="relative z-[2] max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
      <div className="max-w-2xl">
        <p className="eyebrow text-sand/60 mb-4 flex items-center gap-2"><Building2 className="w-4 h-4 text-terra" /> Associations · entreprises · centres</p>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Vous souhaitez accueillir le tambour dans votre structure&nbsp;?</h2>
        <p className="text-sand/70 mt-4 leading-relaxed">Ludovic se déplace dans vos locaux pour animer un cercle ou organiser un stage de création, sur proposition adaptée à votre groupe.</p>
      </div>
      <Link to="/tambour-pour-structures" data-testid="stage-structures-link" className="group inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-terra text-earth text-sm shrink-0 hover:bg-sand transition-colors duration-300">
        Découvrir l’offre structures <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  </section>
);

export const StageTambourContent = () => {
  const [bases, setBases] = useState(FALLBACK);
  useEffect(() => { api.stagePricing().then((p) => setBases(p.bases)).catch(() => {}); }, []);
  useStageSeo(bases);
  return (
    <main className="bg-sand" data-testid="stage-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36 pb-8">
        <p className="eyebrow mb-4">Atelier d'Orbe · Suisse romande</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bark leading-[1.05]" data-testid="stage-title">Créez votre tambour traditionnel</h1>
        <p className="text-fumee mt-5 max-w-2xl leading-relaxed">Une journée accompagnée par Ludovic, du choix de la peau aux premiers battements. Aucune expérience nécessaire : vous repartez avec le tambour créé de vos mains.</p>
      </section>
      <section id="reserver" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 scroll-mt-24" data-testid="stage-reservation">
        <div className="bg-card border border-sand-dark p-5 sm:p-8 lg:p-10"><StageReservation /></div>
      </section>
      <Infos b={bases} />
      <StructuresLink />
      <MoreInfo />
    </main>
  );
};

export default function StageTambour() {
  return <StageTambourContent />;
}
